import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Building2, Search, Plus, RefreshCw, Pause, Play, RotateCcw, Pencil, X, Save, Calendar, Check
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_COLORS = {
  free: "bg-slate-600 text-slate-200",
  basic: "bg-blue-600 text-blue-100",
  standard: "bg-purple-600 text-purple-100",
  pro: "bg-amber-600 text-amber-100",
  enterprise: "bg-emerald-600 text-emerald-100",
};

const STATUS_COLORS = {
  active: "bg-emerald-500/20 text-emerald-400",
  suspended: "bg-red-500/20 text-red-400",
  trial: "bg-amber-500/20 text-amber-400",
};

/* ════════════════════════════════════════════════════════
   Create Org Modal
   ════════════════════════════════════════════════════════ */
function CreateOrgModal({ isOpen, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    plan: "free",
    owner_email: "",
    owner_name: "",
    owner_password: "",
    business_line: "notary_public",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API}/superadmin/organizations`, form, { withCredentials: true });
      onCreated();
      onClose();
      setForm({ name: "", plan: "free", owner_email: "", owner_name: "", owner_password: "", business_line: "notary_public" });
    } catch (err) {
      setError(err.response?.data?.detail || "Error creating organization");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full p-6">
        <h2 className="text-xl font-bold text-white mb-4">Nueva Organización</h2>
        
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Nombre</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm text-slate-400 mb-1">Plan</label>
            <select
              value={form.plan}
              onChange={(e) => setForm({ ...form, plan: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            >
              <option value="free">Free</option>
              <option value="basic">Basic ($19/user)</option>
              <option value="standard">Standard ($39/user)</option>
              <option value="pro">Pro ($59/user)</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>

          <div className="border-t border-slate-700 pt-4 mt-4">
            <h3 className="text-sm font-medium text-slate-300 mb-3">Usuario Owner</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Nombre</label>
                <input
                  type="text"
                  value={form.owner_name}
                  onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                  required
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Email</label>
                <input
                  type="email"
                  value={form.owner_email}
                  onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                  required
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-sm text-slate-400 mb-1">Contraseña</label>
              <input
                type="password"
                value={form.owner_password}
                onChange={(e) => setForm({ ...form, owner_password: e.target.value })}
                required
                minLength={8}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 text-slate-400 hover:text-white border border-slate-700 rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50"
            >
              {loading ? "Creando..." : "Crear"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   Edit Org Modal
   ════════════════════════════════════════════════════════ */
function EditOrgModal({ org, onClose, onUpdated }) {
  const [form, setForm] = useState({
    name: "",
    plan: "",
    status: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (org) {
      setForm({
        name: org.name || "",
        plan: org.plan || "free",
        status: org.status || "active",
      });
      setError(null);
      setSuccess(null);
    }
  }, [org]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {};
      if (form.name !== org.name) payload.name = form.name;
      if (form.plan !== org.plan) payload.plan = form.plan;
      if (form.status !== org.status) payload.status = form.status;

      if (Object.keys(payload).length === 0) {
        setError("No hay cambios");
        setLoading(false);
        return;
      }

      const res = await axios.put(
        `${API}/superadmin/organizations/${org.organization_id}`,
        payload,
        { withCredentials: true }
      );
      setSuccess(res.data.changes?.join(", ") || "Actualizado");
      setTimeout(() => {
        onUpdated();
        onClose();
      }, 800);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al actualizar");
    } finally {
      setLoading(false);
    }
  };

  if (!org) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold text-white">Editar Organización</h2>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <div className="mb-4 p-3 bg-slate-800/50 rounded-lg">
          <p className="text-xs text-slate-500">ID</p>
          <p className="text-sm text-slate-300 font-mono">{org.organization_id}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-300 text-sm">
            ✅ {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Nombre</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Plan</label>
            <select
              value={form.plan}
              onChange={(e) => setForm({ ...form, plan: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            >
              <option value="free">Free</option>
              <option value="basic">Basic ($19/user)</option>
              <option value="standard">Standard ($39/user)</option>
              <option value="pro">Pro ($59/user)</option>
              <option value="enterprise">Enterprise</option>
            </select>
            {form.plan !== org.plan && (
              <p className="mt-1 text-xs text-amber-400">
                Cambio: {org.plan} → {form.plan} (se aplicarán los nuevos límites automáticamente)
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Estado</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            >
              <option value="active">Activo</option>
              <option value="suspended">Suspendido</option>
              <option value="trial">Trial</option>
            </select>
            {form.status !== org.status && (
              <p className="mt-1 text-xs text-amber-400">
                Cambio: {org.status} → {form.status}
              </p>
            )}
          </div>

          {/* Summary of current limits */}
          <div className="border-t border-slate-700 pt-4">
            <p className="text-xs text-slate-500 mb-2">Límites actuales ({org.plan_name || org.plan})</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-slate-800/50 rounded p-2">
                <p className="text-slate-500">Clientes</p>
                <p className="text-white font-medium">{org.limits?.max_clients ?? "—"}</p>
              </div>
              <div className="bg-slate-800/50 rounded p-2">
                <p className="text-slate-500">Usuarios</p>
                <p className="text-white font-medium">{org.limits?.max_users ?? "—"}</p>
              </div>
              <div className="bg-slate-800/50 rounded p-2">
                <p className="text-slate-500">Storage</p>
                <p className="text-white font-medium">{org.limits?.max_storage_mb ? `${org.limits.max_storage_mb} MB` : "—"}</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 text-slate-400 hover:text-white border border-slate-700 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Guardar cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   Main Page
   ════════════════════════════════════════════════════════ */
export default function SuperAdminOrganizations() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [editingTrial, setEditingTrial] = useState(null);
  const [newTrialDate, setNewTrialDate] = useState("");

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (planFilter) params.append("plan", planFilter);
      if (statusFilter) params.append("status", statusFilter);
      
      const res = await axios.get(`${API}/superadmin/organizations?${params}`, { withCredentials: true });
      setOrganizations(res.data.items);
    } catch (err) {
      console.error("Error fetching organizations:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [search, planFilter, statusFilter]);

  const handleAction = async (orgId, action) => {
    setActionLoading(orgId);
    try {
      if (action === "suspend") {
        await axios.post(`${API}/superadmin/organizations/${orgId}/suspend`, {}, { withCredentials: true });
      } else if (action === "reactivate") {
        await axios.post(`${API}/superadmin/organizations/${orgId}/reactivate`, {}, { withCredentials: true });
      } else if (action === "reset-usage") {
        await axios.post(`${API}/superadmin/organizations/${orgId}/reset-usage`, {}, { withCredentials: true });
      }
      fetchOrganizations();
    } catch (err) {
      alert(err.response?.data?.detail || "Error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleEditTrial = (orgId, currentTrialDate) => {
    setEditingTrial(orgId);
    // Format date to YYYY-MM-DD for input
    if (currentTrialDate) {
      const date = new Date(currentTrialDate);
      const formatted = date.toISOString().split('T')[0];
      setNewTrialDate(formatted);
    } else {
      // Default to 14 days from now
      const date = new Date();
      date.setDate(date.getDate() + 14);
      const formatted = date.toISOString().split('T')[0];
      setNewTrialDate(formatted);
    }
  };

  const handleSaveTrial = async (orgId) => {
    try {
      await axios.put(
        `${API}/superadmin/organizations/${orgId}/trial`,
        { trial_ends_at: newTrialDate },
        { withCredentials: true }
      );
      alert("Fecha de trial actualizada exitosamente");
      setEditingTrial(null);
      fetchOrganizations();
    } catch (err) {
      alert(err.response?.data?.detail || "Error al actualizar fecha de trial");
    }
  };

  const handleCancelTrialEdit = () => {
    setEditingTrial(null);
    setNewTrialDate("");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Organizaciones</h1>
          <p className="text-slate-400 mt-1">Gestiona todas las organizaciones</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg"
        >
          <Plus size={18} />
          Nueva
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
          />
        </div>
        <select
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
        >
          <option value="">Todos los planes</option>
          <option value="free">Free</option>
          <option value="basic">Basic</option>
          <option value="standard">Standard</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
        >
          <option value="">Todos</option>
          <option value="active">Activas</option>
          <option value="suspended">Suspendidas</option>
          <option value="trial">Trial</option>
        </select>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-800/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Organización</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Plan</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Estado</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Trial Expira</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Users</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Clientes</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center">
                  <RefreshCw className="w-6 h-6 text-amber-400 animate-spin mx-auto" />
                </td>
              </tr>
            ) : organizations.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  No se encontraron organizaciones
                </td>
              </tr>
            ) : (
              organizations.map((org) => (
                <tr key={org.organization_id} className="hover:bg-slate-800/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{org.name}</p>
                        <p className="text-xs text-slate-500">{org.organization_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${PLAN_COLORS[org.plan] || PLAN_COLORS.free}`}>
                      {org.plan_name || org.plan}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[org.status] || STATUS_COLORS.active}`}>
                      {org.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {editingTrial === org.organization_id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={newTrialDate}
                          onChange={(e) => setNewTrialDate(e.target.value)}
                          className="text-xs px-2 py-1 bg-slate-800 border border-slate-700 rounded text-white"
                        />
                        <button
                          onClick={() => handleSaveTrial(org.organization_id)}
                          className="p-1 text-emerald-400 hover:bg-emerald-500/10 rounded"
                          title="Guardar"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={handleCancelTrialEdit}
                          className="p-1 text-slate-400 hover:bg-slate-700 rounded"
                          title="Cancelar"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {org.trial_ends_at ? (
                          <>
                            <span className="text-slate-300 text-sm">
                              {new Date(org.trial_ends_at).toLocaleDateString('es-MX', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </span>
                            <button
                              onClick={() => handleEditTrial(org.organization_id, org.trial_ends_at)}
                              className="p-1 text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded"
                              title="Modificar fecha de trial"
                            >
                              <Calendar size={14} />
                            </button>
                          </>
                        ) : (
                          <span className="text-slate-500 text-sm">—</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-slate-300">{org.user_count}</td>
                  <td className="px-4 py-3 text-center text-slate-300">{org.customer_count}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setEditOrg(org)}
                        title="Editar"
                        className="p-2 text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg"
                      >
                        <Pencil size={16} />
                      </button>
                      {org.status === "active" || org.status === "trial" ? (
                        <button
                          onClick={() => handleAction(org.organization_id, "suspend")}
                          disabled={actionLoading === org.organization_id}
                          title="Suspender"
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                        >
                          <Pause size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAction(org.organization_id, "reactivate")}
                          disabled={actionLoading === org.organization_id}
                          title="Reactivar"
                          className="p-2 text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg"
                        >
                          <Play size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => handleAction(org.organization_id, "reset-usage")}
                        disabled={actionLoading === org.organization_id}
                        title="Reset uso"
                        className="p-2 text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg"
                      >
                        <RotateCcw size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <CreateOrgModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={fetchOrganizations}
      />

      <EditOrgModal
        org={editOrg}
        onClose={() => setEditOrg(null)}
        onUpdated={fetchOrganizations}
      />
    </div>
  );
}
